import { useState, useEffect, useCallback } from 'react';

/**
 * Hook para manejar favoritos de artículos en localStorage
 * 
 * Los favoritos se almacenan por ID de artículo
 * Formato: agustinos_favorites = ['123', '456', '789']
 */

const FAVORITES_KEY = 'agustinos_favorites';

export const useFavorites = () => {
  const [favorites, setFavorites] = useState(() => {
    try {
      const stored = localStorage.getItem(FAVORITES_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error cargando favoritos:', error);
      return [];
    }
  });

  // Guardar en localStorage cuando cambian los favoritos
  useEffect(() => {
    try {
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
    } catch (error) {
      console.error('Error guardando favoritos:', error);
    }
  }, [favorites]);

  // Verificar si un artículo es favorito
  const isFavorite = useCallback((articleId) => {
    return favorites.includes(String(articleId));
  }, [favorites]);

  // Agregar a favoritos
  const addFavorite = useCallback((articleId) => {
    const id = String(articleId);
    setFavorites(prev => {
      if (prev.includes(id)) {
        return prev; // Ya está en favoritos
      }
      return [...prev, id];
    });
  }, []);

  // Remover de favoritos
  const removeFavorite = useCallback((articleId) => {
    const id = String(articleId);
    setFavorites(prev => prev.filter(fav => fav !== id));
  }, []);

  // Toggle favorito (agregar/remover)
  const toggleFavorite = useCallback((articleId) => {
    const id = String(articleId);
    if (isFavorite(id)) {
      removeFavorite(id);
      return false; // Ahora NO es favorito
    } else {
      addFavorite(id);
      return true; // Ahora SÍ es favorito
    }
  }, [isFavorite, addFavorite, removeFavorite]);

  // Obtener artículos favoritos de una lista
  const getFavoriteArticles = useCallback((articles) => {
    if (!articles || articles.length === 0) return [];
    return articles.filter(article => isFavorite(article.id));
  }, [isFavorite]);

  // Limpiar todos los favoritos
  const clearFavorites = useCallback(() => {
    setFavorites([]);
    localStorage.removeItem(FAVORITES_KEY);
  }, []);

  return {
    favorites,              // Array de IDs favoritos
    isFavorite,            // (id) => boolean
    addFavorite,           // (id) => void
    removeFavorite,        // (id) => void
    toggleFavorite,        // (id) => boolean (retorna nuevo estado)
    getFavoriteArticles,   // (articles[]) => articles[]
    clearFavorites,        // () => void
    favoritesCount: favorites.length
  };
};

export default useFavorites;

